import { useRef } from 'react'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import Image from '@tiptap/extension-image'
import { defaultMarkdownSerializer } from 'prosemirror-markdown'
import type { MarkdownSerializerState } from 'prosemirror-markdown'
import type { Node as PMNode } from '@tiptap/pm/model'
import { useLang } from '../../i18n'
import { formatImageTitle, parseImageTitle } from '../../lib/image-geometry'
import { applyWidth, useWidthResize } from './width-resize'

/**
 * The image node view: four corner drag handles, a width control, and a delete button.
 *
 * THE WIDTH LIVES IN THE MARKDOWN, in the image's title slot as a `=NN%` token — see
 * `lib/image-geometry.ts` for why nothing else survives. tiptap's own `width`/`height`
 * attributes (and its built-in resize node view) are deliberately unused: they render fine and
 * then vanish on the next reload, because the serializer cannot express them.
 *
 * The gesture, the arithmetic and the controls are shared with the diagram node view — see
 * `width-resize.tsx`. This file owns only the codec and the commit.
 */
function ImageNodeView({ node, deleteNode, selected, editor, getPos, updateAttributes }: NodeViewProps) {
  const { t } = useLang()
  const src = (node.attrs.src as string | null) ?? ''
  const alt = (node.attrs.alt as string | null) ?? ''
  // Only the human half of the title is ever shown; the token must not leak into a tooltip.
  const geometry = parseImageTitle(node.attrs.title as string | null)
  const editable = editor.isEditable

  const wrapperRef = useRef<HTMLDivElement>(null)

  /** Write ONE width into the title slot, and show what actually landed. */
  const commit = (pct: number | null): void => {
    updateAttributes({ title: formatImageTitle({ ...geometry, widthPct: pct }) })
    // SectionNumbers' filterTransaction drops (and toasts) a write into a § someone else holds —
    // updateAttributes is an ordinary transaction, so it is refused like any other. Re-read what
    // actually landed rather than leaving the wrapper showing a width the document does not have.
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const pos = getPos()
    const live = pos == null ? null : editor.state.doc.nodeAt(pos)
    applyWidth(wrapper, parseImageTitle((live?.attrs.title as string | null) ?? null).widthPct)
  }

  const { corners, tools } = useWidthResize({
    wrapperRef,
    widthPct: geometry.widthPct,
    editable,
    commit,
    labels: {
      corner: {
        tl: t('img_resize_tl'),
        tr: t('img_resize_tr'),
        bl: t('img_resize_bl'),
        br: t('img_resize_br'),
      },
      field: t('img_width'),
      dec: t('img_width_dec'),
      inc: t('img_width_inc'),
    },
  })

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={'img-nv' + (selected ? ' sel' : '') + (geometry.widthPct == null ? '' : ' sized')}
    >
      <img src={src} alt={alt} title={geometry.title === '' ? undefined : geometry.title} />
      {editable && (
        <>
          {corners}
          <div className="img-tools" role="group" aria-label={t('img_tools')}>
            {tools}
            <button
              className="img-del"
              type="button"
              title={t('img_delete')}
              aria-label={t('img_delete')}
              onClick={() => deleteNode()}
            >
              ✕
            </button>
          </div>
        </>
      )}
    </NodeViewWrapper>
  )
}

/** Image extension with the delete + resize node view. */
export const ImageBlock = Image.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      // tiptap-markdown serialises `image` with prosemirror-markdown's INLINE serializer, which
      // never closes the block. Registered as a block node (RichEditor: `inline: false`), the
      // next block would then be glued onto the image line (`![](u)### Heading`). Same output
      // (the title slot carrying the `=NN%` token stays byte-identical), plus the block close.
      // backend/adapters/markdown/image-markdown.ts is the server's copy; the shared
      // md-canonical fixture pins the two byte for byte.
      markdown: {
        serialize(state: MarkdownSerializerState, node: PMNode, parent: PMNode, index: number) {
          defaultMarkdownSerializer.nodes.image!(state, node, parent, index)
          state.closeBlock(node)
        },
        parse: {
          // handled by markdown-it: a stand-alone `![…](…)` line already parses as a block image
        },
      },
    }
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView)
  },
})
